import React from 'react';
import PropTypes from 'prop-types';

const DEFAULT_PALETTE = [
  '#3b82f6', // blue (dominant)
  '#f97316', // orange (secondary)
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
];

/**
 * Maps item colors with special overrides to match the telemetry aesthetic
 */
function getItemColor(item, index, category) {
  if (category === 'locations' || category === 'location') {
    const lbl = (item.label || '').toLowerCase();
    if (lbl.includes('unknown')) return '#60728c'; // steel slate for unknown
    if (lbl.includes('india')) return '#10b981'; // green for India
    if (lbl.includes('us') || lbl.includes('united states')) return '#f59e0b'; // amber for US
  }
  return DEFAULT_PALETTE[index % DEFAULT_PALETTE.length];
}

/**
 * Renders an individual diagnostic donut card with SVG arc segments,
 * center dominant metrics, and high-density session legends in a clean light theme.
 */
export default function EnvironmentDonutCard({
  title,
  items = [],
  loading = false,
  category = '',
  emptyMessage = 'No telemetry recorded',
  testId = 'environment-donut-card'
}) {
  if (loading) {
    return (
      <div
        className="w-full min-w-0 bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 flex flex-col justify-between h-full animate-pulse"
        data-testid={`${testId}-loading`}
      >
        <div className="h-4 w-28 bg-slate-200 rounded" />
        <div className="flex items-center justify-center my-6">
          <div className="w-32 h-32 rounded-full border-8 border-slate-200" />
        </div>
        <div className="flex flex-col gap-3 pt-3 border-t border-slate-200">
          <div className="flex justify-between items-center">
            <div className="h-3 w-20 bg-slate-200 rounded" />
            <div className="h-4 w-12 bg-slate-200 rounded" />
          </div>
          <div className="flex justify-between items-center">
            <div className="h-3 w-16 bg-slate-200 rounded" />
            <div className="h-4 w-12 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const safeItems = Array.isArray(items) ? items : [];
  const dominantItem = safeItems[0] || null;
  const dominantPercentage = dominantItem ? Number(dominantItem.percentage ?? 0).toFixed(1) : '0';
  const dominantLabel = dominantItem?.label || '—';

  // SVG Geometry constants
  const R = 38;
  const C = 2 * Math.PI * R; // ~238.761

  let accumulatedLength = 0;

  return (
    <div
      className="w-full min-w-0 bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 flex flex-col justify-between h-full transition-all duration-200 hover:border-slate-300 hover:shadow-sm"
      data-testid={testId}
    >
      {/* Card Header Title */}
      <h3 className="text-slate-700 font-semibold text-sm tracking-tight m-0 mb-3">
        {title}
      </h3>

      {/* Donut Graphic */}
      {safeItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center my-8 text-center text-slate-400 text-xs">
          <p className="m-0">{emptyMessage}</p>
        </div>
      ) : (
        <div className="relative flex items-center justify-center my-2">
          <svg
            className="w-32 h-32 transform -rotate-90"
            viewBox="0 0 100 100"
            aria-label={`${title} donut breakdown`}
            role="img"
          >
            {/* Background track circle */}
            <circle
              cx="50"
              cy="50"
              r={R}
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="9"
            />

            {/* Data arc segments */}
            {safeItems.map((item, idx) => {
              const rawPct = Math.max(0, Math.min(100, Number(item.percentage ?? 0)));
              const arcLength = (rawPct / 100) * C;
              const strokeOffset = -accumulatedLength;
              accumulatedLength += arcLength;
              const color = getItemColor(item, idx, category);

              return (
                <circle
                  key={`${item.label}-${idx}`}
                  cx="50"
                  cy="50"
                  r={R}
                  fill="none"
                  stroke={color}
                  strokeWidth="9"
                  strokeDasharray={`${arcLength} ${C}`}
                  strokeDashoffset={strokeOffset}
                  strokeLinecap="butt"
                  className="transition-all duration-500 ease-out"
                />
              );
            })}
          </svg>

          {/* Centered Dominant Metric */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none select-none">
            <span
              className="text-base font-bold text-slate-900 leading-none tracking-tight"
              data-testid={`${testId}-dominant-pct`}
            >
              {dominantPercentage}%
            </span>
            <span
              className="text-[11px] font-medium text-slate-500 mt-1 max-w-[70px] truncate leading-none"
              title={dominantLabel}
              data-testid={`${testId}-dominant-label`}
            >
              {dominantLabel}
            </span>
          </div>
        </div>
      )}

      {/* Detailed Legend Rows */}
      {safeItems.length > 0 && (
        <div className="flex flex-col gap-2 mt-4 pt-3 border-t border-slate-200" role="list">
          {safeItems.map((item, idx) => {
            const color = getItemColor(item, idx, category);
            const count = Number(item.count ?? 0);
            const percentage = Number(item.percentage ?? 0).toFixed(1);

            return (
              <div
                key={`${item.label}-${idx}`}
                className="flex items-center justify-between py-1"
                role="listitem"
                data-testid={`donut-legend-item-${category}-${(item.label || '').toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                {/* Dot & Label */}
                <div className="flex items-center gap-2 min-w-0 pr-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  <span
                    className="text-sm font-medium text-slate-700 truncate"
                    title={item.label}
                  >
                    {item.label}
                  </span>
                </div>

                {/* Percentage & Session Count */}
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-slate-900 leading-tight">
                    {percentage}%
                  </div>
                  <div className="text-[11px] text-slate-500 font-normal leading-tight mt-0.5">
                    {count.toLocaleString()} {count === 1 ? 'session' : 'sessions'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

EnvironmentDonutCard.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      count: PropTypes.number,
      percentage: PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  category: PropTypes.string,
  emptyMessage: PropTypes.string,
  testId: PropTypes.string
};
